import Link from 'next/link';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { usePageLoadBi } from '../../../lib/use-page-load-bi';
import { useBasePath } from '../../../lib/use-base-path';

// Truncated bezier: starts where the full conductor stroke starts, bows through
// the belly, then lifts before reaching the upper-right resolve.
const TRUNCATED_PATH = 'M 12 18 C 4 38, 22 62, 48 56';
const TRUNCATED_LENGTH = 78;

// The conversation in the URL doesn't exist on the active metasite (stale link,
// deleted thread, bogus GUID). The pen-stroke draws itself then lifts off
// unfinished — the gesture tells the story so the words can stay short.
export function MissingMovementHero({ narrow }: { narrow: boolean }) {
  const { t } = useTranslation();
  const productName = useProductName();
  const basePath = useBasePath();
  usePageLoadBi({ page_name: 'chat_home', page_version: 'v3_chat_home', state: 'missing_conversation' });
  const strokeSize = narrow ? 180 : 240;
  return (
    <div style={{ ...WRAP, padding: narrow ? '40px 24px 56px' : '64px 48px 80px' }}>
      <svg width={strokeSize} height={strokeSize * 0.7} viewBox="0 0 100 70" fill="none" aria-hidden>
        <defs>
          <linearGradient id="missing-trace-grad" x1="0" y1="70" x2="100" y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="var(--ds-fill-accent-brand-default)" stopOpacity="0.9" />
            <stop offset="60%" stopColor="var(--ds-green-neon-300)" stopOpacity="0.8" />
            <stop offset="100%" stopColor="var(--ds-green-neon-600)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d={TRUNCATED_PATH}
          stroke="url(#missing-trace-grad)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          style={{ strokeDasharray: TRUNCATED_LENGTH, animation: 'symphony-missing-trace 1.6s ease-in-out forwards' }}
        />
      </svg>
      <div style={{ ...TITLE, fontSize: narrow ? 22 : 26 }}>{t('chat.missing.symphony.title')}</div>
      <div style={BODY}>{t('chat.missing.symphony.body')}</div>
      <Link href="/" as={basePath} style={BACK_LINK}>
        {t('chat.missing.symphony.back', { productName })}
      </Link>
    </div>
  );
}

const WRAP: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 26,
  textAlign: 'center',
};

const TITLE: React.CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontWeight: 400,
  letterSpacing: '-0.005em',
  color: 'rgba(var(--ink-rgb), 0.85)',
  lineHeight: 1.25,
};

const BODY: React.CSSProperties = {
  fontSize: 14,
  color: 'rgba(var(--ink-rgb), 0.5)',
  lineHeight: 1.55,
  maxWidth: 440,
};

const BACK_LINK: React.CSSProperties = {
  marginTop: 6,
  padding: '10px 18px',
  borderRadius: 'var(--sym-radius-button)',
  fontSize: 13,
  fontWeight: 'var(--sym-weight-medium)',
  letterSpacing: '0.01em',
  textDecoration: 'none',
  color: 'var(--sym-on-accent)',
  background: 'var(--sym-accent)',
  boxShadow: 'none',
};
